import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { UserUtilityService } from 'src/app/services/user-utility.service';
import { UnitType } from '../../../../models/enums/unit-type.enum';
import { IceapiService, ManagementApiService } from '../../../../services';
import { DataService } from '../../../../services/data.service';
import { MeasurementService } from '../../../../services/measurement.service';
import { ProcessInfoData } from '../../models/process-info-data';
import { ProcessInfoModel } from '../../models/process-info-model';
import { pipeSpecVersion } from 'src/app/Modules/settings/models/idms/pipe-specification.model';
import { IdmsSharedService } from 'src/app/Modules/idms/services/idms-shared.service';
import { JointCalcService } from 'src/app/Modules/idms/components/plant-manager/services/joint-calc-service.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-process-information-input',
    templateUrl: './process-information-input.component.html',
    styleUrls: ['./process-information-input.component.scss'],
})
export class ProcessInformationInputComponent implements OnInit {
    @Input() isProcessInfoEnabled: boolean = true;
    @Input() isRedirect: boolean;
    @Input() enable: boolean;
    @Input() pipeSpecVersion: pipeSpecVersion;
    @Input()
    set processInfoInput(value: ProcessInfoModel) {
        this._processInfoInput = value;
        if (value != undefined) {
            this.getPressureTempRatings();
        }
    }
    get processInfoInput(): ProcessInfoModel {
        return this._processInfoInput;
    }
    @Output() returnData: EventEmitter<ProcessInfoData> = new EventEmitter();
    destroy$ = new Subject();

    tooltip_position = 'above';
    operatingTempDefault: string;
    operatingPressureDefault: string;
    testPressureDefault: string;
    processInfoFormGroup: UntypedFormGroup;
    processInfoData: ProcessInfoData;
    pressureTempRatings: any;
    CONST_ZERO: number;
    processInputData: any = {};
    private _processInfoInput: ProcessInfoModel;

    constructor(
        private fb: UntypedFormBuilder,
        private measSvc: MeasurementService,
        private iceapi: IceapiService,
        private dataSvc: DataService,
        private mgmtSvc: ManagementApiService,
        private user_utility: UserUtilityService,
        public router: Router,
        private globalEmitter: GlobalEmittersService,
        public idmsSharedService: IdmsSharedService,
        private jointCalcService: JointCalcService
    ) {
        this.CONST_ZERO = 0;
    }

    ngOnInit(): void {
        this.processInfoData = new ProcessInfoData();
        this.loadFormGroup();
        this.initForm();

        if (!this.isProcessInfoEnabled) {
            this.processInfoFormGroup.get('operatingTemp').disable();
            this.processInfoFormGroup.get('operatingPressure').disable();
            this.processInfoFormGroup.get('testPressure').disable();
        }

        this.processInfoFormGroup
            .get('operatingTemp')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                if (value)
                    this.processInfoFormGroup.get('operatingTemp').setValue(this.measSvc.format(value, UnitType.DefaultTemperatureUnit), { emitEvent: false });
            });

        this.processInfoFormGroup
            .get('operatingPressure')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                if (value) this.checkAndUpdateUnit(value, UnitType.DefaultPressureUnit, 'operatingPressure');
            });

        this.processInfoFormGroup
            .get('testPressure')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                if (value) this.checkAndUpdateUnit(value, UnitType.DefaultPressureUnit, 'testPressure');
            });
        this.pressureTempRatings = [];
        this.processInfoFormGroup.setValue(this.processInfoFormGroup.value);
        this.processInfoFormGroup.updateValueAndValidity();
        this.processInfoFormGroup.markAllAsTouched();
        if (this.idmsSharedService.enableConfirmWithRouter(2) == true) {
            // Subscribe to valueChanges and emit the form when it changes
            this.processInfoFormGroup.valueChanges.subscribe((formValue) => {
                this.btnProcessInfoConfirmClicked();
            });
        }

        this.jointCalcService.selectedTighteningSpecification.pipe(takeUntil(this.destroy$)).subscribe(async (ts) => {
            if (ts) {
                if (this.jointCalcService.pipeSpecList.length > 1) {
                    this.jointCalcService.PipeSpecFilteredList = this.jointCalcService.filterList(this.jointCalcService.pipeSpecpayload.lookupParams);
                }

                if (this.jointCalcService.pipeSpecList.length) {
                    if (this.jointCalcService.pipeSpecList[this.jointCalcService.pipeSpecList.length - 1]?.calculationOverrides?.operatingTemperature) {
                        this.processInfoFormGroup
                            .get('operatingTemp')
                            .setValue(
                                this.jointCalcService.pipeSpecList[this.jointCalcService.pipeSpecList.length - 1]?.calculationOverrides?.operatingTemperature
                            );
                    }

                    if (this.jointCalcService.pipeSpecList[this.jointCalcService.pipeSpecList.length - 1]?.calculationOverrides?.operatingPressure) {
                        this.processInfoFormGroup
                            .get('operatingPressure')
                            .setValue(
                                this.jointCalcService.pipeSpecList[this.jointCalcService.pipeSpecList.length - 1]?.calculationOverrides?.operatingPressure
                            );
                    }

                    if (this.jointCalcService.pipeSpecList[this.jointCalcService.pipeSpecList.length - 1]?.calculationOverrides?.testPressure) {
                        this.processInfoFormGroup
                            .get('testPressure')
                            .setValue(this.jointCalcService.pipeSpecList[this.jointCalcService.pipeSpecList.length - 1]?.calculationOverrides?.testPressure);
                    }
                }
            }
        });
    }

    initForm() {
        this.processInfoFormGroup = this.fb.group({
            operatingTemp: [
                this.operatingTempDefault,
                {
                    updateOn: 'blur',
                },
            ],
            operatingPressure: [
                this.operatingPressureDefault,
                {
                    updateOn: 'blur',
                },
            ],
            testPressure: [
                this.testPressureDefault,
                {
                    updateOn: 'blur',
                },
            ],
        });
        this.globalEmitter.processInfoForm.next(this.processInfoFormGroup);
        this.processInfoFormGroup.markAllAsTouched();
        this.processInfoFormGroup.markAsDirty();
    }

    loadFormGroup() {
        if (this.isRedirect && this.dataSvc.calculationData != null) {
            this.operatingTempDefault = this.dataSvc.calculationData.input.calculationOverrides.operatingTemperature;
            this.operatingPressureDefault = this.dataSvc.calculationData.input.calculationOverrides.operatingPressure;
            this.testPressureDefault = this.dataSvc.calculationData.input.calculationOverrides.testPressure;

            //Assiging id when getting data from matrix
            this.processInputData.flangeMaterialId = this.pipeSpecVersion?.recordId?.flangeMaterialId1;
            this.processInputData.pressureClassValueId = this.pipeSpecVersion?.recordId?.pressureClassId;
            if (this.processInputData.flangeMaterialId && this.processInputData.pressureClassValueId) {
                this.getPressureTempRatings();
            }
        }
        if (this.idmsSharedService.enableConfirmWithRouter(2) == true && this.pipeSpecVersion) {
            this.operatingTempDefault = this.pipeSpecVersion?.calculationOverrides?.operatingTemperature;
            this.operatingPressureDefault = this.pipeSpecVersion?.calculationOverrides?.operatingPressure;
            this.testPressureDefault = this.pipeSpecVersion?.calculationOverrides?.testPressure;
        }
    }

    async checkAndUpdateUnit(value: string, unitKey: UnitType, controlName: string) {
        if (this.processInfoFormGroup.get(controlName).value) {
            this.processInfoFormGroup.get(controlName).setValue(this.measSvc.format(value, unitKey), { emitEvent: false });
        }
    }

    btnProcessInfoConfirmClicked(): void {
        this.processInfoFormGroup.markAllAsTouched();
        if (!this.isProcessInfoEnabled) {
            if (this.processInfoFormGroup.valid) {
                this.processInfoData.isDataValid = true;
                this.returnData.emit(this.processInfoData);
            } else {
                this.processInfoData.isDataValid = false;
            }
        } else {
            this.processInfoFormGroup.markAllAsTouched();
            if (this.processInfoFormGroup.valid) {
                this.processInfoData.operatingTemperature = this.processInfoFormGroup.get('operatingTemp').value;
                this.processInfoData.operatingPressure = this.processInfoFormGroup.get('operatingPressure').value;
                this.processInfoData.testPressure = this.processInfoFormGroup.get('testPressure').value;
                this.processInfoData.isDataValid = true;
                this.returnData.emit(this.processInfoData);
            } else {
                this.processInfoData.isDataValid = false;
            }
        }
    }

    getPressureTempRatings(
        Temperature = null,
        testPressure = null,
        opratingPressure = null,
        maxAllowableOperatingPressure = null,
        maxAllowableTestPressure = null,
        unit = null
    ) {
        if (this.processInfoInput !== undefined) {
            let obj: any = {};
            obj.pressureClassValueId = this.processInfoInput.pressureClassId;
            obj.flangeMaterialId = this.processInfoInput.flangeMaterialId;
            if (Temperature) {
                obj.Temperature = Temperature;
            }
            if (testPressure) {
                obj.testPressure = testPressure;
            }
            if (opratingPressure) {
                obj.OperatingPressure = opratingPressure;
            }
            if (maxAllowableOperatingPressure) {
                obj.MaxAllowableOperatingPressure = maxAllowableOperatingPressure;
            }
            if (maxAllowableTestPressure) {
                obj.MaxAllowableTestPressure = maxAllowableTestPressure;
            }
            this.getPressureTemparingApi(obj, unit);
        } else if (Object.keys(this.processInputData).length > 0) {
            if (Temperature) {
                this.processInputData.Temperature = Temperature;
            }
            if (testPressure) {
                this.processInputData.testPressure = testPressure;
            }
            if (opratingPressure) {
                this.processInputData.OperatingPressure = opratingPressure;
            }
            if (maxAllowableOperatingPressure) {
                this.processInputData.MaxAllowableOperatingPressure = maxAllowableOperatingPressure;
            }
            if (maxAllowableTestPressure) {
                this.processInputData.MaxAllowableTestPressure = maxAllowableTestPressure;
            }
            this.getPressureTemparingApi(this.processInputData, unit);
        }
    }

    getPressureTemparingApi(payload, unit = null) {
        if (
            this.processInfoInput !== undefined &&
            this.processInfoInput.pressureClassId !== undefined &&
            this.processInfoInput.flangeMaterialId !== undefined &&
            this.processInfoInput.standardId !== undefined
        ) {
            this.iceapi
                .getPressureTempRatings(payload)
                .toPromise()
                .then(
                    (res) => {
                        if (res.success === true) {
                            this.pressureTempRatings = res.response;

                            let temperature = this.getTempAndPressureValue(0, unit).tmpValue;
                            let pressure = this.getTempAndPressureValue(1, unit).opratingPessureValue;
                            let testPressure = this.getTempAndPressureValue(2, unit).testPressureValue;

                            this.processInfoFormGroup.get('operatingPressure').clearValidators();
                            this.processInfoFormGroup.get('testPressure').clearValidators();

                            // this.processInfoFormGroup.get('operatingPressure').updateValueAndValidity();
                            // this.processInfoFormGroup.get('testPressure').updateValueAndValidity();
                            let temp = temperature.toString().replace(/\s+/g, '').trim();
                            let emitFlag = false;
                            if (this.router.url == '/idms/plantmanager') {
                                emitFlag = true;
                            }
                            this.processInfoFormGroup.get('operatingTemp').setValue(this.measSvc.format(temp, UnitType.DefaultTemperatureUnit, 2));

                            this.processInfoFormGroup
                                .get('operatingPressure')
                                .setValue(this.measSvc.format(pressure.toString(), UnitType.DefaultPressureUnit, 2));

                            this.processInfoFormGroup
                                .get('testPressure')
                                .setValue(this.measSvc.format(testPressure.toString(), UnitType.DefaultPressureUnit, 2));

                            if (this.router.url == '/informate/standardflange/true' || this.router.url == '/informate/standardflange/false') {
                                this.settingValidator(1);
                                this.settingValidator(2);
                            }
                        } else {
                            this.user_utility.errorModelPopup(res.message);
                        }
                    },
                    (err) => {
                        // this.user_utility.errorModelPopup(err)
                        console.warn(err);
                    }
                );
        }
    }
    // * helper method to get the pressure and temperature values
    getTempAndPressureValue(index: number, unit) {
        let tmpValue = 0;
        let opratingPessureValue = 0;
        let testPressureValue = 0;
        switch (index) {
            // * temperature
            case 0:
                let defaultUnitForTemperature = this.mgmtSvc.userDefaults?.find(
                    (o) => o.userPreferenceKey == UnitType[UnitType.DefaultTemperatureUnit]
                )?.userPreference;
                tmpValue =
                    defaultUnitForTemperature.toLowerCase() == 'celcius'
                        ? this.pressureTempRatings.temperature.metric.value
                        : this.pressureTempRatings.temperature.imperial.value;

                if (unit) {
                    tmpValue =
                        unit.toLowerCase().indexOf('c') != -1
                            ? this.pressureTempRatings.temperature.metric.value
                            : this.pressureTempRatings.temperature.imperial.value;
                }
                break;

            // * Oprating  pressure
            case 1:
                let defaultUnitForPressure = this.mgmtSvc.userDefaults?.find(
                    (o) => o.userPreferenceKey == UnitType[UnitType.DefaultPressureUnit]
                )?.userPreference;
                opratingPessureValue =
                    defaultUnitForPressure.toLowerCase() == 'psi'
                        ? this.pressureTempRatings.operatingPressure.imperial.value
                        : this.pressureTempRatings.operatingPressure.metric.value;
                break;

            // * Oprating  pressure
            case 2:
                let defaultUnitForPressure1 = this.mgmtSvc.userDefaults?.find(
                    (o) => o.userPreferenceKey == UnitType[UnitType.DefaultPressureUnit]
                )?.userPreference;
                testPressureValue =
                    defaultUnitForPressure1.toLowerCase() == 'psi'
                        ? this.pressureTempRatings.testPressure.imperial.value
                        : this.pressureTempRatings.testPressure.metric.value;
                break;
        }
        return { tmpValue, opratingPessureValue, testPressureValue };
    }

    onBlurEvent(event: any, index) {
        if (this.router.url == '/informate/standardflange/true' || this.router.url == '/informate/standardflange/false') {
            let resp = event.target.value.split(' ');
            let unit = resp[1];
            this.settingValidator(index, unit);
        }
    }

    settingValidator(index, changeunit = null) {
        let maxTempature = 0;
        let maxOperatingPressure = 0;
        let maxAllowableTestPressure = 0;

        switch (index) {
            case 0:
                let defaultUnitForTemperature = this.mgmtSvc.userDefaults?.find(
                    (o) => o.userPreferenceKey == UnitType[UnitType.DefaultTemperatureUnit]
                )?.userPreference;

                maxTempature = defaultUnitForTemperature.toLowerCase() == 'celcius' ? 1000 : 1832;

                if (changeunit) {
                    maxTempature = changeunit.toLowerCase().indexOf('c') != -1 ? 1000 : 1832;
                }
                this.processInfoFormGroup.get('operatingTemp').setValidators([Validators.required, Validators.max(maxTempature)]);
                this.processInfoFormGroup.get('operatingTemp').updateValueAndValidity();
                break;
            case 1:
                let defaultUnitForPressure = this.mgmtSvc.userDefaults?.find(
                    (o) => o.userPreferenceKey == UnitType[UnitType.DefaultPressureUnit]
                )?.userPreference;
                maxOperatingPressure =
                    defaultUnitForPressure.toLowerCase() == 'psi'
                        ? this.pressureTempRatings.maxAllowableOperatingPressure.imperial.value
                        : this.pressureTempRatings.maxAllowableOperatingPressure.metric.value;

                if (changeunit) {
                    maxOperatingPressure =
                        changeunit.toLowerCase().indexOf('psi') != -1
                            ? this.pressureTempRatings.maxAllowableOperatingPressure.imperial.value
                            : this.pressureTempRatings.maxAllowableOperatingPressure.metric.value;
                }
                let res: string = this.processInfoFormGroup.get('operatingPressure').value;
                if (res != null || res != undefined) {
                    let res1 = res.split(' ');
                    this.processInfoFormGroup.get('operatingPressure').invalid;
                    this.processInfoFormGroup.get('operatingPressure').setValidators([Validators.required, Validators.max(maxOperatingPressure)]);
                    this.processInfoFormGroup.get('operatingPressure').updateValueAndValidity();
                }

                break;
            case 2:
                let defaultUnitForPressure1 = this.mgmtSvc.userDefaults?.find(
                    (o) => o.userPreferenceKey == UnitType[UnitType.DefaultPressureUnit]
                )?.userPreference;
                maxAllowableTestPressure =
                    defaultUnitForPressure1.toLowerCase() == 'psi'
                        ? this.pressureTempRatings.maxAllowableTestPressure.imperial.value
                        : this.pressureTempRatings.maxAllowableTestPressure.metric.value;

                if (changeunit) {
                    maxAllowableTestPressure =
                        changeunit.toLowerCase().indexOf('psi') != -1
                            ? this.pressureTempRatings.maxAllowableTestPressure.imperial.value
                            : this.pressureTempRatings.maxAllowableTestPressure.metric.value;
                }
                let resp: string = this.processInfoFormGroup.get('testPressure').value;
                if (resp != null || resp != undefined) {
                    let resp1 = resp.split(' ');
                    this.processInfoFormGroup.get('testPressure').invalid;
                    this.processInfoFormGroup.get('testPressure').setValidators([Validators.required, Validators.max(maxAllowableTestPressure)]);
                    this.processInfoFormGroup.get('testPressure').updateValueAndValidity();
                }
                break;
        }
    }

    onBlurOptTemp($event, index) {
        this.processInfoFormGroup
            .get('operatingTemp')
            .setValue(this.measSvc.format($event.target.value, UnitType.DefaultTemperatureUnit), { emitEvent: false });
        let maxTempature = 0;
        if (this.router.url == '/informate/standardflange/false' || this.router.url == '/informate/standardflange/true') {
            let testPressure = this.processInfoFormGroup.get('testPressure').value;
            let OpratingPressure = this.processInfoFormGroup.get('operatingPressure').value;
            let defaultUnitForPressure = this.mgmtSvc.userDefaults?.find((o) => o.userPreferenceKey == UnitType[UnitType.DefaultPressureUnit])?.userPreference;
            let maxAllowableOperatingPressure =
                defaultUnitForPressure.toLowerCase() == 'psi'
                    ? this.pressureTempRatings.maxAllowableOperatingPressure.imperial.stringValue
                    : this.pressureTempRatings.maxAllowableOperatingPressure.metric.stringValue;
            let defaultUnitForPressure1 = this.mgmtSvc.userDefaults?.find((o) => o.userPreferenceKey == UnitType[UnitType.DefaultPressureUnit])?.userPreference;
            let maxAllowableTestPressure =
                defaultUnitForPressure1.toLowerCase() == 'psi'
                    ? this.pressureTempRatings.maxAllowableTestPressure.imperial.stringValue
                    : this.pressureTempRatings.maxAllowableTestPressure.metric.stringValue;
            let val = this.processInfoFormGroup.get('operatingTemp').value;
            let resp = $event.target.value.split(' ');
            let unit = resp[1];
            if (resp[0] != '') {
                if (unit == '°F') {
                    maxTempature = 1832;
                } else {
                    maxTempature = 1000;
                }
                if (resp[0] <= maxTempature || this.processInfoFormGroup.get('operatingTemp').value == '') {
                    this.getPressureTempRatings(val, testPressure, OpratingPressure, maxAllowableOperatingPressure, maxAllowableTestPressure, unit);
                }
            }

            this.settingValidator(index, unit);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
